import { ChangeEvent, KeyboardEventHandler, useEffect, useRef, useState } from 'react';

import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { Alert, TextareaAutosize, Tooltip } from '@mui/material';
import html2canvas from 'html2canvas';

import { APIObjectType } from '@api/ApiTypes';

import Button from '@components/Button';
import ChatItem from '@components/experiments/plutoAI/ChatItem';
import PlotCardView from '@components/plots/PlotCardView';

import useComments from '@hooks/useComments';
import usePlot from '@hooks/usePlot';
import useSampleData from '@hooks/useSampleData';

import AnnotationSet from '@models/Annotation';
import { CommentObjectType } from '@models/Comment';
import Experiment from '@models/Experiment';
import Plot from '@models/Plot';
import { Chat } from '@models/Thread';

import { PlutoIntelligenceIcon } from '../../icons/custom/PlutoIntelligenceIcon';
import { LoadingIndicator } from '../../LoadingButton';

type SaveAsNoteButtonProps = {
    messages: { content: string; is_assistant: boolean }[];
    objectType: CommentObjectType;
    objectUuid: string;
};

const SaveAsNoteButton = ({ messages, objectType, objectUuid }: SaveAsNoteButtonProps) => {
    const { postNewComment } = useComments({
        object_type: objectType,
        object_uuid: objectUuid,
    });
    const [saved, setSaved] = useState(false);
    const [saving, setSaving] = useState(false);

    const handleSave = async () => {
        setSaving(true);
        const text = messages
            .map((msg) => (msg.is_assistant ? 'AI: ' : 'User: ') + msg.content.replace(/\n/g, ' ').trim())
            .join('\n\n');
        try {
            await postNewComment({ content: text, comment_type: 'note', is_private: false });
            setSaved(true);
            setTimeout(() => setSaved(false), 1500);
        } catch {
            setSaved(false);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Button
            variant="outlined"
            color="secondary"
            size="small"
            className="text-xs"
            onClick={handleSave}
            style={{ minWidth: 100 }}
            disabled={saving}
        >
            {saved ? 'Saved!' : 'Save as Note'}
        </Button>
    );
};

// CopyThreadButton: copies all chat messages to clipboard
const CopyThreadButton = ({ messages }: { messages: { content: string; is_assistant: boolean }[] }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
        const text = messages
            .map((msg) => (msg.is_assistant ? 'AI: ' : 'User: ') + msg.content.replace(/\n/g, ' ').trim())
            .join('\n\n');
        try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch {
            setCopied(false);
        }
    };

    return (
        <Button
            variant="outlined"
            color="secondary"
            size="small"
            className="text-xs"
            onClick={handleCopy}
            style={{ minWidth: 80 }}
        >
            {copied ? 'Copied!' : 'Copy Thread'}
        </Button>
    );
};

type Props = {
    plot: Plot | AnnotationSet;
    experiment: Experiment;
    objectType?: APIObjectType | 'plot' | 'clusterannotationset';
};

const PlutoAIGeminiPlotView = ({ plot: initialPlot, experiment, objectType }: Props) => {
    const chatWindow = useRef<null | HTMLDivElement>(null);
    const lastAIMessageRef = useRef<HTMLDivElement | null>(null);
    const plotScreenshotRef = useRef<HTMLDivElement | null>(null);
    const textareaRef = useRef<HTMLTextAreaElement | null>(null);
    const [newChat, setNewChat] = useState<string>('');
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [messages, setMessages] = useState<Chat[]>([]);
    const [shouldScrollToTopOnNextAI, setShouldScrollToTopOnNextAI] = useState(false);

    // Pick 3 random prompts per experiment/plot and persist in sessionStorage
    const [randomPrompts, setRandomPrompts] = useState<string[]>([]);
    useEffect(() => {
        const allPrompts = [
            'Summarize the key findings and biological significance of this plot.',
            'Identify any genes or pathways that stand out and explain their potential roles.',
            'Suggest follow-up experiments or analyses based on the data shown in this plot.',
            'Explain any unexpected trends or outliers in the data.',
            'How do the results in this plot compare to the control or baseline group?',
            'What are the potential clinical or practical implications of these findings?',
            'Are there any data quality issues or limitations visible in this plot?',
            'Which samples or groups are most distinct in this visualization?',
            'What statistical methods were likely used to generate this plot?',
            'How might sample size or composition affect the interpretation of this plot?',
            'What additional metadata would help interpret these results?',
            'Can you suggest a hypothesis that could be tested based on this plot?',
            'What are the next steps for validating the findings shown here?',
        ];
        const key = `pluto-ai-prompts-${experiment?.uuid || ''}-${initialPlot?.uuid || ''}`;
        const stored = sessionStorage.getItem(key);
        if (stored) {
            try {
                const parsed = JSON.parse(stored);
                if (Array.isArray(parsed) && parsed.length === 3) {
                    setRandomPrompts(parsed);
                    return;
                }
            } catch {}
        }
        // Generate and store new prompts
        const shuffled = [...allPrompts];
        for (let i = shuffled.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }
        const selected = shuffled.slice(0, 3);
        setRandomPrompts(selected);
        sessionStorage.setItem(key, JSON.stringify(selected));
        // Only re-run if experiment or plot changes
    }, [experiment?.uuid, initialPlot?.uuid]);
    const { plot, plotData, plotDataLoading } = usePlot({
        plotId: initialPlot.uuid,
        experiment,
        sortDataBy: 'padj',
        dataLimit: 500,
    });
    const { data: sampleData } = useSampleData({ experiment });

    const handleChangeChat = (e: ChangeEvent<HTMLTextAreaElement>) => {
        if (error) setError(null);
        setNewChat(e.target.value);
    };

    const handleChatKeyDown: KeyboardEventHandler<HTMLTextAreaElement> = (event) => {
        if (!event.shiftKey && event.key === 'Enter') {
            event.preventDefault();
            return handleSendChat();
        }
    };

    // Helper to subset plotData to top N rows by Adj_P_Value
    function subsetPlotDataByAdjPValue(plotData: any, limit: number = 200) {
        if (!plotData || !plotData.items || !plotData.items.Adj_P_Value || !Array.isArray(plotData.items.Adj_P_Value)) {
            return plotData;
        }
        const n = Math.min(limit, plotData.items.Adj_P_Value.length);
        // Create array of indices and sort by Adj_P_Value ascending
        const indices = plotData.items.Adj_P_Value.map((v: number, i: number) => i)
            .sort((a: number, b: number) => plotData.items.Adj_P_Value[a] - plotData.items.Adj_P_Value[b])
            .slice(0, n);

        // For each key in items, subset the array to the top N indices
        const subsetItems: Record<string, any[]> = {};
        for (const key of Object.keys(plotData.items)) {
            const arr = plotData.items[key];
            if (Array.isArray(arr)) {
                subsetItems[key] = indices.map((i: number) => arr[i]);
            } else {
                subsetItems[key] = arr;
            }
        }
        return {
            ...plotData,
            items: subsetItems,
            count: n,
        };
    }

    const handleSendChat = async (promptOverride?: string) => {
        const promptToSend = promptOverride !== undefined ? promptOverride : newChat;
        if (!promptToSend.trim()) return;

        const userMessage: Chat = {
            uuid: `user-${Date.now()}`,
            content: promptToSend,
            is_assistant: false,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            is_archived: false,
            type: 'text',
        };

        setMessages((prev) => [...prev, userMessage]);
        setNewChat('');
        // Refocus the textarea for follow-up chats
        if (textareaRef.current) {
            textareaRef.current.focus();
        }
        setLoading(true);
        setError(null);

        // Subset plotData before sending to Gemini
        const subsettedPlotData = subsetPlotDataByAdjPValue(plotData, 500);

        // Base prompt for Gemini
        const basePrompt =
            'You are a scientific researcher helping to give a researcher more context about their experiment. You are given data about the experiment, the plot, the top 500 results from the results data sorted by adj p value, and a plot screenshot. Please help the user understand their data.';

        // Gather experiment info and analysis/methods
        const experimentInfo: Record<string, any> = {
            name: experiment?.name,
            description: experiment?.description,
            type: experiment?.type,
        };

        // Add methods and analysis info from experiment and plot, keeping both if present
        if (experiment && typeof experiment === 'object' && 'methods' in experiment) {
            experimentInfo.methods = experiment.methods;
        }
        if (plot && typeof plot === 'object') {
            if ('analysis_type' in plot) {
                experimentInfo.analysis_type = (plot as any).analysis_type;
            }
            if ('methods' in plot) {
                experimentInfo.plot_methods = (plot as any).methods;
            }
        }

        // Take screenshot of the plot using html2canvas
        let plotScreenshot: string | null = null;
        if (plotScreenshotRef.current) {
            try {
                const canvas = await html2canvas(plotScreenshotRef.current, { useCORS: true });
                plotScreenshot = canvas.toDataURL('image/png');
            } catch {
                // If screenshot fails, continue without it
                plotScreenshot = null;
            }
        }

        try {
            const response = await fetch('/api/vertex/vertex-gemini', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    prompt: promptToSend,
                    basePrompt: basePrompt,
                    model: 'gemini-2.0-flash',
                    plot: plot,
                    plotData: subsettedPlotData,
                    experimentInfo: experimentInfo,
                    image: plotScreenshot,
                    sampleData: sampleData,
                }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to fetch response from Gemini');
            }

            const data = await response.json();
            const content = data.response.response[0].text;
            const aiMessage: Chat = {
                uuid: `ai-${Date.now()}`,
                content,
                is_assistant: true,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
                is_archived: false,
                type: 'text',
            };
            setShouldScrollToTopOnNextAI(true);
            setMessages((prev) => [...prev, aiMessage]);
        } catch (e: any) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    };

    const ThinkingAnimation = () => {
        const [dotCount, setDotCount] = useState(1);
        useEffect(() => {
            const interval = setInterval(() => {
                setDotCount((prevDotCount) => (prevDotCount % 3) + 1);
            }, 800);
            return () => clearInterval(interval);
        }, []);
        const dots = '.'.repeat(dotCount);
        return <p className="text-sm text-default animate-pulse ml-2">thinking{dots}</p>;
    };

    // Handle predefined prompt click: send immediately
    const handlePromptClick = (prompt: string) => {
        if (!loading && !plotDataLoading) {
            handleSendChat(prompt);
        }
    };

    // Scroll chat to bottom for user, or to the most recent AI message after new AI response
    useEffect(() => {
        if (messages.length > 0) {
            const lastMsg = messages[messages.length - 1];
            if (shouldScrollToTopOnNextAI && lastMsg.is_assistant && lastAIMessageRef.current) {
                lastAIMessageRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
                setShouldScrollToTopOnNextAI(false);
            } else if (chatWindow.current && !lastMsg.is_assistant) {
                chatWindow.current.scrollTop = chatWindow.current.scrollHeight;
            }
        }
    }, [messages, shouldScrollToTopOnNextAI]);

    // Import PlotCardView and render it, passing plotScreenshotRef as plotThumbnailRef
    // (Assume PlotCardView is imported at the top: import PlotCardView from '@components/plots/PlotCardView';)

    return (
        <>
            {error ? (
                <div className="m-2 mb-0 rounded">
                    <Alert severity="error">{error}</Alert>
                </div>
            ) : null}
            {loading && !messages.length ? (
                <div className="absolute left-0 top-0 flex h-full w-full items-center justify-center bg-white opacity-50">
                    <LoadingIndicator size={48} />
                </div>
            ) : null}
            {/* Render the plot for screenshotting */}
            <div
                style={{
                    position: 'absolute',
                    left: '-9999px',
                    top: 0,
                    width: 800,
                    height: 600,
                    pointerEvents: 'none',
                }}
            >
                <PlotCardView
                    plot={plot ?? null}
                    experiment={experiment}
                    plotThumbnailRef={plotScreenshotRef}
                    // Pass any other required props as needed
                />
            </div>
            <div className="my-2 flex flex-col w-full h-full p-4 pb-2 pl-8 bg-white rounded shadow">
                <div className="flex items-center justify-between">
                    <div className="flex items-baseline gap-2">
                        <h1 className="text-xl font-semibold tracking-tight inline-flex items-center gap-2">
                            <PlutoIntelligenceIcon height={26} width={26} />
                            AI Chat
                            <Tooltip
                                title="This AI chat uses the Google Gemini 2.0 Flash model. No data is saved or used in AI training."
                                placement="top"
                                arrow
                            >
                                <span>
                                    <HelpOutlineIcon fontSize="small" className="text-gray-400 cursor-pointer" />
                                </span>
                            </Tooltip>
                        </h1>
                    </div>
                    <div className="flex gap-2">
                        <CopyThreadButton messages={messages} />
                        <SaveAsNoteButton
                            messages={messages}
                            objectType={(objectType as CommentObjectType) || 'plot'}
                            objectUuid={plot?.uuid || ''}
                        />
                    </div>
                </div>

                <div className="flex-1 my-2 flex flex-col overflow-auto pb-2" ref={chatWindow}>
                    {messages.map((message, index) => {
                        // Attach ref to the most recent AI message
                        const isLastAI =
                            message.is_assistant && index === messages.map((m) => m.is_assistant).lastIndexOf(true);
                        return (
                            <div key={index} ref={isLastAI ? lastAIMessageRef : undefined}>
                                <ChatItem message={message} threadId="gemini-thread" />
                            </div>
                        );
                    })}
                </div>
                {/* Input area fixed at the bottom */}
                <div className="sticky bottom-0 left-0 right-0 bg-white pt-2 z-10">
                    {/* Predefined prompt buttons */}
                    {messages.length === 0 && (
                        <div className="mb-2 flex flex-wrap gap-2">
                            {randomPrompts.map((prompt, idx) => (
                                <Button
                                    key={idx}
                                    variant="outlined"
                                    color="secondary"
                                    size="small"
                                    className="text-xs"
                                    onClick={() => handlePromptClick(prompt)}
                                    disabled={loading || plotDataLoading}
                                >
                                    {prompt}
                                </Button>
                            ))}
                        </div>
                    )}
                    <TextareaAutosize
                        minRows={2}
                        maxRows={4}
                        className="no_margin mb-0 w-full rounded border-slate-300"
                        onChange={handleChangeChat}
                        onKeyDown={handleChatKeyDown}
                        value={newChat}
                        placeholder="Ask a question about this plot..."
                        disabled={loading || plotDataLoading}
                        ref={textareaRef}
                    />
                    <div className="flex flex-row items-center mb-4">
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => handleSendChat()}
                            disabled={!newChat || loading || plotDataLoading}
                            size="small"
                        >
                            Submit
                        </Button>
                        {loading && <ThinkingAnimation />}
                    </div>
                </div>
            </div>
        </>
    );
};

export default PlutoAIGeminiPlotView;
